/**
 * Presentational primitives for the wardrobe module.
 * Module-local by convention — every module here keeps its own ui.tsx.
 */
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { ApiRequestError } from '../../lib/api';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

export function errorText(err: unknown): string {
  if (err instanceof ApiRequestError) {
    if (err.isForbidden) return 'That is not your wardrobe.';
    if (err.status === 404) {
      return 'The wardrobe API is not available on this server yet. If this is the live site, the latest deploy may still be building.';
    }
    return err.message || 'The request failed.';
  }
  return 'Could not reach the household server. Check your connection and try again.';
}

// ---------------------------------------------------------------- data loading

interface Resource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /**
   * Patch what is already loaded, without a refetch.
   *
   * `reload()` flips `loading` back to true, and every panel here renders its body behind
   * `data && !loading`, so a reload unmounts that whole subtree and remounts it. For a
   * background refresh that is invisible; for a refresh triggered while a FORM is open it
   * throws away the form's state mid-edit. Use this when a write has already returned the
   * updated row and the server has nothing further to tell us.
   */
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

export function useResource<T>(load: () => Promise<T>, deps: unknown[]): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadRef
      .current()
      .then((r) => !cancelled && setData(r))
      .catch((e) => !cancelled && setError(errorText(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return {
    data,
    loading,
    error,
    reload: useCallback(() => setNonce((n) => n + 1), []),
    setData,
  };
}

// ---------------------------------------------------------------- colour

/**
 * Garment colour names to something renderable.
 *
 * Seeded from the palette in the style guide, so the words Brian actually uses —
 * olive, tan, camel, cream — land on the right swatch. Colours are free text by
 * design (a garment is "washed olive", not a hex code), so an unknown name is normal
 * and falls back to a neutral chip. The NAME is always shown next to the swatch:
 * the colour is decoration, the text is the data.
 */
const COLOR_MAP: Record<string, string> = {
  olive: '#4f553c',
  army: '#4f553c',
  green: '#4f553c',
  sage: '#7c8467',
  tan: '#b08d5f',
  camel: '#b08d5f',
  khaki: '#c3a987',
  sand: '#c3a987',
  beige: '#c3a987',
  cream: '#e8dfce',
  oatmeal: '#e8dfce',
  ecru: '#e8dfce',
  brown: '#6b4a32',
  chocolate: '#5a3d29',
  rust: '#a0522d',
  charcoal: '#3a3a38',
  grey: '#6e6f6b',
  gray: '#6e6f6b',
  heather: '#8d8e8a',
  black: '#1c1b1a',
  white: '#f5f3ef',
  navy: '#2e3e52',
  indigo: '#2e3e52',
  denim: '#5a7391',
  blue: '#5a7391',
  burgundy: '#6e2639',
  red: '#8c3b31',
};

export function colorHex(name: string): string | null {
  const key = name.trim().toLowerCase();
  if (COLOR_MAP[key]) return COLOR_MAP[key];
  // "washed olive", "dark brown" — match on any word we know.
  for (const word of key.split(/[\s/-]+/)) {
    if (COLOR_MAP[word]) return COLOR_MAP[word];
  }
  return null;
}

/**
 * The photo URL for a garment, cache-busted on the stored key.
 *
 * `GET /items/:id/photo` serves `Cache-Control: private, max-age=3600` from a URL that does
 * NOT change when the photo does. The worker deliberately writes a fresh R2 key on every
 * upload so a cached response can never show the previous garment — but that protection is
 * defeated one layer up by the stable URL, and replacing a photo would show the old one for
 * up to an hour. The key's tail is enough to make each version a distinct URL.
 */
export function photoSrc(item: { id: number; image_r2_key: string | null }): string {
  const base = `/api/wardrobe/items/${item.id}/photo`;
  return item.image_r2_key ? `${base}?v=${encodeURIComponent(item.image_r2_key.slice(-12))}` : base;
}

/** Same cache-busting rule for a want's photo. See photoSrc above for why it is needed. */
export function gapPhotoSrc(gap: { id: number; image_r2_key: string | null }): string {
  const base = `/api/wardrobe/gaps/${gap.id}/photo`;
  return gap.image_r2_key ? `${base}?v=${encodeURIComponent(gap.image_r2_key.slice(-12))}` : base;
}

/** A background for one or two colours. Two splits the chip rather than picking one. */
export function swatchStyle(colors: string[]): React.CSSProperties {
  const hexes = colors.map(colorHex).filter((h): h is string => h !== null);
  if (hexes.length === 0) return { backgroundColor: 'var(--color-surface-sunk)' };
  if (hexes.length === 1) return { backgroundColor: hexes[0] };
  return { background: `linear-gradient(135deg, ${hexes[0]} 0 50%, ${hexes[1]} 50% 100%)` };
}

// ---------------------------------------------------------------- surfaces

export type Tone = 'surface' | 'sunk' | 'ok' | 'near' | 'over';

const CARD_TONES: Record<Tone, string> = {
  surface: 'border-border bg-surface',
  sunk: 'border-border bg-surface-sunk',
  ok: 'border-sage/40 bg-sage-soft',
  near: 'border-honey/40 bg-honey-soft',
  over: 'border-clay/40 bg-clay-soft',
};

export function Card({
  children,
  className,
  tone = 'surface',
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  tone?: Tone;
  as?: 'div' | 'section' | 'li';
}) {
  return (
    <Tag className={cx('rounded-card border p-4 sm:p-5', CARD_TONES[tone], className)}>
      {children}
    </Tag>
  );
}

export function PanelHeading({
  title,
  sub,
  action,
}: {
  title: string;
  sub?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="font-display text-xl font-semibold text-ink">{title}</h2>
        {sub && <p className="mt-1 max-w-prose text-sm text-ink-soft">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------- controls

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
};

const BUTTON_STYLES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-sage text-white hover:bg-sage/90 border-transparent',
  secondary: 'bg-surface text-ink border-border hover:bg-surface-sunk',
  ghost: 'bg-transparent text-ink-soft border-transparent hover:bg-surface-sunk hover:text-ink',
  danger: 'bg-transparent text-clay border-border hover:bg-clay-soft',
};

export function Button({ variant = 'secondary', className, ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cx(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-full border px-4 text-sm font-medium',
        'transition-colors focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
        'focus-visible:ring-offset-canvas focus-visible:outline-none disabled:opacity-50',
        BUTTON_STYLES[variant],
        className
      )}
    />
  );
}

/** Label via htmlFor, hint via aria-describedby — never nested inside the label. */
export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<{ id?: string; 'aria-describedby'?: string }>, {
        id,
        'aria-describedby': hint ? hintId : undefined,
      })
    : children;

  return (
    <div className={cx('block', className)}>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink">
        {label}
      </label>
      {control}
      {hint && (
        <span id={hintId} className="mt-1 block text-xs text-ink-soft">
          {hint}
        </span>
      )}
    </div>
  );
}

export const inputClass =
  'block min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-base text-ink ' +
  'placeholder:text-ink-faint focus-visible:border-sage focus-visible:ring-2 focus-visible:ring-sage/40 ' +
  'focus-visible:outline-none';

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(inputClass, props.className)} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cx(inputClass, 'min-h-20 py-2', props.className)} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(inputClass, 'pr-8', props.className)} />;
}

// ---------------------------------------------------------------- states

export function Loading({ label, rows = 6 }: { label: string; rows?: number }) {
  return (
    <div role="status" aria-live="polite" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="aspect-square animate-pulse rounded-card border border-border bg-surface-sunk"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card tone="over">
      <p className="text-sm font-medium text-ink">Something went wrong</p>
      <p className="mt-1 text-sm text-ink-soft">{message}</p>
      <Button variant="secondary" className="mt-3" onClick={onRetry}>
        Try again
      </Button>
    </Card>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Card tone="sunk" className="text-center">
      <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
      <div className="mx-auto mt-2 max-w-prose text-sm leading-relaxed text-ink-soft">
        {children}
      </div>
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </Card>
  );
}

export type ChipTone = 'neutral' | 'ok' | 'near' | 'over' | 'dusk';

export function Chip({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: ChipTone;
  /** Native tooltip. Used where a chip is shorthand for something that needs explaining. */
  title?: string;
}) {
  const tones: Record<ChipTone, string> = {
    neutral: 'bg-surface-sunk text-ink-soft border-border',
    ok: 'bg-sage-soft text-ink border-sage/40',
    near: 'bg-honey-soft text-ink border-honey/50',
    over: 'bg-clay-soft text-ink border-clay/50',
    dusk: 'bg-dusk-soft text-ink border-dusk/40',
  };
  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        tones[tone]
      )}
    >
      {children}
    </span>
  );
}
